import { 
  Recommendation, 
  RecommendationFilters, 
  RecommendationSummary, 
  RecommendationDeployment,
  RecommendationError,
  ApiResponse 
} from '../types';

class RecommendationService {
  private baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

  private async apiRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const token = localStorage.getItem('auth_token');
    
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    };

    try {
      console.log('RecommendationService: Making API request to', `${this.baseUrl}${endpoint}`);
      const response = await fetch(`${this.baseUrl}${endpoint}`, config);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('RecommendationService: API request failed', {
          status: response.status,
          statusText: response.statusText,
          error: errorText
        });
        
        throw new Error(`HTTP ${response.status}: ${response.statusText} - ${errorText}`);
      }

      const result = await response.json();
      console.log('RecommendationService: API request successful', result);
      
      return result;
    } catch (error) {
      console.error('RecommendationService: API request error', error);
      throw error;
    }
  }

  async getRecommendations(filters?: RecommendationFilters): Promise<Recommendation[]> {
    try {
      const params = new URLSearchParams();

      if (filters) {
        if (filters.cloud?.length) {
          params.append('clouds', filters.cloud.join(','));
        }
        if (filters.category?.length) {
          params.append('categories', filters.category.join(','));
        }
        if (filters.priority?.length) {
          params.append('priorities', filters.priority.join(','));
        }
        if (filters.status?.length) {
          params.append('statuses', filters.status.join(','));
        }
        if (filters.minSavings) {
          params.append('minSavings', filters.minSavings.toString());
        }
        if (filters.maxEffort) {
          params.append('maxEffort', filters.maxEffort);
        }
      }

      const queryString = params.toString();
      const endpoint = `/api/cost/recommendations${queryString ? `?${queryString}` : ''}`;

      const response = await this.apiRequest<ApiResponse<Recommendation[]>>(endpoint);

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to fetch recommendations');
      }

      return response.data;
    } catch (error: any) {
      console.error('RecommendationService: Error fetching recommendations:', error);
      throw this.createRecommendationError(error.message);
    }
  }

  async getRecommendationById(recommendationId: string): Promise<Recommendation> {
    try {
      const endpoint = `/api/cost/recommendations/${recommendationId}`;

      const response = await this.apiRequest<ApiResponse<Recommendation>>(endpoint);

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to fetch recommendation');
      }

      return response.data;
    } catch (error: any) {
      console.error('RecommendationService: Error fetching recommendation by ID:', error);
      throw this.createRecommendationError(error.message, recommendationId);
    }
  }

  async getRecommendationSummary(): Promise<RecommendationSummary> {
    try {
      const response = await this.apiRequest<ApiResponse<RecommendationSummary>>('/api/cost/recommendations/summary');
      
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to fetch recommendation summary');
      }

      return response.data;
    } catch (error: any) {
      console.error('RecommendationService: Error fetching recommendation summary:', error);
      throw this.createRecommendationError(error.message);
    }
  }

  async generateRecommendations(forceRefresh = false): Promise<any> {
    try {
      const endpoint = `/api/cost/recommendations/generate`;

      const response = await this.apiRequest<ApiResponse<any>>(endpoint, {
        method: 'POST',
        body: JSON.stringify({})
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to generate recommendations');
      }

      return response.data;
    } catch (error: any) {
      console.error('RecommendationService: Error generating recommendations:', error);
      throw this.createRecommendationError(error.message);
    }
  }

  async deployRecommendation(recommendationId: string): Promise<RecommendationDeployment> {
    try {
      const response = await this.apiRequest<ApiResponse<RecommendationDeployment>>(
        `/api/cost/deploy-recommendation`,
        {
          method: 'POST',
          body: JSON.stringify({ recommendationId })
        }
      );
      
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to deploy recommendation');
      }

      return response.data;
    } catch (error: any) {
      console.error('RecommendationService: Error deploying recommendation:', error);
      throw this.createRecommendationError(error.message, recommendationId);
    }
  }

  async getDeploymentStatus(deploymentId: string): Promise<RecommendationDeployment> {
    try {
      const response = await this.apiRequest<ApiResponse<RecommendationDeployment>>(
        `/api/cost/deployment/${deploymentId}`
      );
      
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to get deployment status');
      }

      return response.data;
    } catch (error: any) {
      console.error('RecommendationService: Error getting deployment status:', error);
      throw this.createRecommendationError(error.message);
    }
  }

  async updateRecommendationStatus(
    recommendationId: string, 
    status: 'approved' | 'rejected'
  ): Promise<Recommendation> {
    try {
      const response = await this.apiRequest<ApiResponse<Recommendation>>(
        `/api/cost/recommendations/${recommendationId}/status`,
        {
          method: 'PUT',
          body: JSON.stringify({ status })
        }
      );
      
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to update recommendation status');
      }

      return response.data;
    } catch (error: any) {
      console.error('RecommendationService: Error updating recommendation status:', error);
      throw this.createRecommendationError(error.message, recommendationId);
    }
  }

  async rollbackDeployment(deploymentId: string): Promise<RecommendationDeployment> {
    try {
      const response = await this.apiRequest<ApiResponse<RecommendationDeployment>>(
        `/api/cost/deployment/${deploymentId}/rollback`,
        {
          method: 'POST'
        }
      );

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to rollback deployment');
      }

      return response.data;
    } catch (error: any) {
      console.error('RecommendationService: Error rolling back deployment:', error);
      throw this.createRecommendationError(error.message);
    }
  }

  async executeDeploymentStep(
    recommendationId: string,
    stepId: string,
    stepAction: any,
    parameters?: any
  ): Promise<any> {
    try {
      const response = await this.apiRequest<ApiResponse<any>>(
        `/api/cost/recommendations/${recommendationId}/execute-step`,
        {
          method: 'POST',
          body: JSON.stringify({
            stepId,
            stepAction,
            parameters: parameters || {}
          })
        }
      );

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to execute deployment step');
      }

      return response.data;
    } catch (error: any) {
      console.error('RecommendationService: Error executing deployment step:', error);
      throw this.createRecommendationError(error.message, recommendationId);
    }
  }

  async rollbackDeploymentStep(
    recommendationId: string,
    stepId: string,
    originalState?: any
  ): Promise<any> {
    try {
      const response = await this.apiRequest<ApiResponse<any>>(
        `/api/cost/recommendations/${recommendationId}/rollback-step`,
        {
          method: 'POST',
          body: JSON.stringify({
            stepId,
            originalState: originalState || {}
          })
        }
      );

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to rollback deployment step');
      }

      return response.data;
    } catch (error: any) {
      console.error('RecommendationService: Error rolling back deployment step:', error);
      throw this.createRecommendationError(error.message, recommendationId);
    }
  }

  private createRecommendationError(message: string, recommendationId?: string): RecommendationError {
    return {
      code: 'RECOMMENDATION_ERROR',
      message,
      recommendationId,
      timestamp: new Date().toISOString()
    };
  }

  // Mock data generators for development/testing
  generateMockRecommendations(): Recommendation[] {
    const now = new Date().toISOString();
    
    return [
      {
        id: 'rec-1',
        title: 'Right-size staging EC2 instances',
        description: 'Staging account has 5 EC2 instances with consistent <5% CPU utilization. Consider downsizing from t3.large to t3.small.',
        type: 'rightsize_instance',
        priority: 'high',
        impact: {
          monthlySavings: 2430,
          annualSavings: 29160,
          currency: 'USD'
        },
        effort: 'low',
        category: 'compute_optimization',
        cloud: 'aws',
        resourceDetails: {
          resourceId: 'i-0123456789abcdef0',
          resourceName: 'staging-web-server-1',
          resourceType: 'EC2 Instance',
          region: 'us-east-1',
          accountId: '123456789013',
          currentConfig: {
            instanceType: 't3.large',
            vcpus: 2,
            memory: '8 GiB'
          },
          recommendedConfig: {
            instanceType: 't3.small',
            vcpus: 2,
            memory: '2 GiB'
          },
          metrics: {
            utilizationRate: 4.2,
            period: '30 days',
            avgCpuUtilization: 4.2,
            avgMemoryUtilization: 12.5
          }
        },
        actions: [
          {
            id: 'action-1',
            type: 'api_call',
            description: 'Stop EC2 instance',
            parameters: { instanceId: 'i-0123456789abcdef0' },
            estimatedDuration: '2 minutes',
            riskLevel: 'low',
            rollbackPossible: true
          },
          {
            id: 'action-2',
            type: 'api_call',
            description: 'Modify instance type to t3.small',
            parameters: { instanceId: 'i-0123456789abcdef0', instanceType: 't3.small' },
            estimatedDuration: '5 minutes',
            riskLevel: 'medium',
            rollbackPossible: true
          },
          {
            id: 'action-3',
            type: 'api_call',
            description: 'Start EC2 instance',
            parameters: { instanceId: 'i-0123456789abcdef0' },
            estimatedDuration: '2 minutes',
            riskLevel: 'low',
            rollbackPossible: false
          }
        ],
        status: 'pending',
        createdAt: now,
        updatedAt: now
      },
      {
        id: 'rec-2',
        title: 'Archive unused GitHub repositories',
        description: 'Found 23 private repositories with no commits in the last 6 months. Consider archiving them to reduce storage costs.',
        type: 'archive_repositories',
        priority: 'medium',
        impact: {
          monthlySavings: 115,
          annualSavings: 1380,
          currency: 'USD'
        },
        effort: 'low',
        category: 'resource_cleanup',
        cloud: 'github',
        resourceDetails: {
          resourceId: 'org/unused-repo-1',
          resourceName: 'unused-legacy-project',
          resourceType: 'Repository',
          currentConfig: {
            status: 'active',
            visibility: 'private',
            lastCommit: '2024-02-15T10:30:00Z'
          },
          metrics: {
            utilizationRate: 0,
            period: '6 months',
            lastAccessDate: '2024-02-15T10:30:00Z'
          }
        },
        actions: [
          {
            id: 'action-1',
            type: 'api_call',
            description: 'Archive repository',
            parameters: { repo: 'org/unused-repo-1' },
            estimatedDuration: '1 minute',
            riskLevel: 'low',
            rollbackPossible: true
          }
        ],
        status: 'pending',
        createdAt: now,
        updatedAt: now
      },
      {
        id: 'rec-3',
        title: 'Move BigQuery datasets to cheaper storage tier',
        description: 'BigQuery dataset "analytics-archive" is queried less than once per month but costs $500/month. Move to coldline storage.',
        type: 'change_storage_tier',
        priority: 'medium',
        impact: {
          monthlySavings: 350,
          annualSavings: 4200,
          currency: 'USD'
        },
        effort: 'medium',
        category: 'storage_optimization',
        cloud: 'gcp',
        resourceDetails: {
          resourceId: 'my-project-prod:analytics_archive',
          resourceName: 'analytics-archive',
          resourceType: 'BigQuery Dataset',
          region: 'us-central1',
          currentConfig: {
            storageClass: 'standard',
            sizeGB: 2500
          },
          recommendedConfig: {
            storageClass: 'coldline',
            sizeGB: 2500
          },
          metrics: {
            utilizationRate: 2,
            period: '3 months',
            queryFrequency: 0.3
          }
        },
        actions: [
          {
            id: 'action-1',
            type: 'api_call',
            description: 'Change BigQuery dataset storage class to coldline',
            parameters: { 
              datasetId: 'analytics_archive',
              storageClass: 'coldline'
            },
            estimatedDuration: '30 minutes',
            riskLevel: 'low',
            rollbackPossible: true
          }
        ],
        status: 'pending',
        createdAt: now,
        updatedAt: now
      }
    ];
  }

  generateMockRecommendationSummary(): RecommendationSummary {
    return {
      total: 12,
      byPriority: {
        high: 3,
        medium: 6,
        low: 3
      },
      byStatus: {
        pending: 8,
        approved: 2,
        in_progress: 1,
        completed: 1,
        rejected: 0,
        failed: 0
      },
      totalPotentialSavings: {
        monthly: 4580,
        annual: 54960,
        currency: 'USD'
      },
      byCloud: {
        aws: 7,
        gcp: 3,
        github: 2
      }
    };
  }
}

export const recommendationService = new RecommendationService();